/**
 * Which listing a store tile IS, or null when the tile cannot say.
 *
 * A store tile is a CATALOG CARD, and a card can carry several of this seller's
 * listings at different conditions and prices -- Near Mint R500 beside Damaged
 * R200. `seller-card-lookup` therefore sends `listing_id` only for a card with
 * exactly one live listing, and null otherwise, so "add this" is never a guess
 * between two conditions the buyer would not see until the cart. Ambiguous
 * tiles keep their existing job: open the card page and choose there.
 *
 * `available` is `Sum(quantity)` on this endpoint, so on a one-listing card it
 * is that listing's stock and doubles as the quantity ceiling. Zero means sold
 * out and there is nothing to add.
 *
 * Pure and exported on its own so the rule is testable without mounting the
 * store page's whole shell.
 *
 * THE STALE-TRACKED-PRICE GUARD. `useAddToCart` reads `pricing_mode`/
 * `price_stale` off the item it is handed, so they are threaded through here.
 * `seller-card-lookup` (this tile's endpoint, `SellerCardLookupListView` in
 * `apps/api/apps/cards/views.py`) serializes an aggregated CATALOG CARD, not
 * a listing, and used to emit neither -- both arrived `undefined`, the guard's
 * predicate was falsy, and a stale TRACKED listing was always added as if it
 * were fine (issue #110). The endpoint now emits them, alongside `listing_id`
 * and from the same resolved listing: present on a one-listing tile, null on
 * an ambiguous one, which has no quick-add to guard anyway.
 *
 * Still read them defensively. This is the AFFORDANCE, not the guarantee --
 * `POST /api/orders/create-order/` refuses a stale TRACKED listing at checkout
 * regardless of what this function returns, and must stay the thing relied on.
 *
 * `card_id` rides along because `refresh-card-prices` is keyed on the CATALOG
 * CARD, not the listing: when the guard finds this item blocked it re-prices
 * the card and re-reads the listing off the response. On this endpoint the row
 * IS the catalog card, so the tile's own `id` is that id.
 *
 * @returns {{id: string, card_id: string, sellerId: string, quantity: number, price: number, pricing_mode?: 'fixed'|'tracked', price_stale?: boolean, price_unconfirmed?: boolean}|null}
 */
export function quickAddListing(card, sellerId) {
  if (!card || !card.listing_id || !sellerId) return null;

  const available = Number(card.available) || 0;
  if (available <= 0) return null;

  return {
    id: card.listing_id,
    card_id: card.id,
    sellerId,
    quantity: available,
    price: card.lowest_price,
    pricing_mode: card.pricing_mode,
    price_stale: card.price_stale,
    /* THE SECOND GUARD FIELD, and this whitelist is where it would go missing.
       `price_unconfirmed` says the card was not ATTEMPTED today, which is a
       different question from `price_stale` (see `utils/priceAvailability.js`)
       -- and BOTH the tile's marker and `useAddToCart`'s add-to-cart gate
       read the UNION of the two off this object. Passed straight through,
       uncoerced:
       `undefined` (an old cached response, or the server flag off before it
       emitted the key at all) means "we were never told", which must not be
       flattened into "checked, and it's fine". */
    price_unconfirmed: card.price_unconfirmed,
  };
}

export default quickAddListing;
